import { useContext } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckIcon from '@mui/icons-material/Check';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useWorkflow } from './WorkflowContext';
import { AuthContext } from 'components/authentication/context/authContext';

type Props = { currentStateId?: number | null; parentDisabled?: boolean; isActionPending?: boolean };

const ScopeList = ({ raw }: { raw?: string | null }) => {
  if (!raw) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const scopes = raw.split(',').map((s) => s.trim()).filter(Boolean);
  return (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {scopes.map((s) => (
        <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
      ))}
    </Box>
  );
};

const Flag = ({ value }: { value?: boolean }) =>
  value
    ? <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
    : <CloseOutlinedIcon sx={{ fontSize: 14, color: 'error.main' }} />;

const WorkflowDebugPanel = ({ currentStateId, parentDisabled, isActionPending }: Props) => {
  const { workflow, loading } = useWorkflow();
  const { userEntityDetails } = useContext(AuthContext);

  const userScopes = (userEntityDetails?.scopes ?? []).map((s) => s.scope).filter(Boolean);
  const currentState = workflow?.states?.find((s) => s.id === currentStateId);
  const availableTransitions = (workflow?.transitions ?? []).filter((t) =>
    currentStateId ? t.workflowStateFrom?.id === currentStateId : t.workflowStateFrom == null
  );

  return createPortal(
    <Box sx={{ position: 'fixed', bottom: 56, left: 16, zIndex: 99999 }}>
      <Paper
        elevation={8}
        sx={{
          p: 1.5,
          maxHeight: '75vh',
          overflowY: 'auto',
          minWidth: 480,
          maxWidth: 560,
          fontSize: 12,
          borderTop: '3px solid',
          borderColor: 'warning.main',
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <BugReportIcon sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography variant="caption" fontWeight="bold">Workflow Debug</Typography>
          {loading && <Chip label="loading…" size="small" sx={{ fontSize: 10, height: 18 }} />}
          {isActionPending && <Chip label="action pending" size="small" color="warning" sx={{ fontSize: 10, height: 18 }} />}
          {parentDisabled && <Chip label="buttons disabled by parent" size="small" color="error" sx={{ fontSize: 10, height: 18 }} />}
        </Box>

        {/* User scopes */}
        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          User scopes ({userScopes.length})
        </Typography>
        {userScopes.length > 0 ? (
          <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
            {userScopes.map((s) => (
              <Chip key={s} label={s} size="small" color="info" sx={{ fontSize: 10, height: 18 }} />
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled" display="block" mb={1}>No scopes</Typography>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Workflow info */}
        <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
          <Chip label={`Module ${workflow?.idModule}`} size="small" color="primary" sx={{ fontSize: 10, height: 18 }} />
          <Chip label={workflow?.name ?? 'unnamed'} size="small" sx={{ fontSize: 10, height: 18 }} />
          {currentState && (
            <Chip
              label={`Current: ${currentState.name ?? currentState.id}`}
              size="small"
              color="warning"
              sx={{ fontSize: 10, height: 18 }}
            />
          )}
          {currentState && (
            <Chip
              label={currentState.editable ? 'Fields: EDITABLE' : 'Fields: READ-ONLY'}
              size="small"
              color={currentState.editable ? 'success' : 'error'}
              sx={{ fontSize: 10, height: 18 }}
            />
          )}
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* States table */}
        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>States</Typography>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 11,
            '& td, & th': { p: '2px 6px', verticalAlign: 'middle' },
            '& th': { fontWeight: 600, color: 'text.secondary', textAlign: 'left', whiteSpace: 'nowrap' },
            '& tr:nth-of-type(even)': { backgroundColor: 'action.hover' },
          }}
        >
          <thead>
            <tr>
              <th>State</th>
              <th title="viewable">V</th>
              <th title="editable">E</th>
              <th title="deletable">D</th>
              <th>viewScopes</th>
              <th>editScopes</th>
              <th>deleteScopes</th>
            </tr>
          </thead>
          <tbody>
            {(workflow?.states ?? []).map((state) => {
              const isCurrent = state.id === currentStateId;
              return (
                <tr key={state.id} style={isCurrent ? { outline: '1px solid orange' } : undefined}>
                  <td>
                    <Typography
                      variant="caption"
                      fontWeight={isCurrent ? 700 : 400}
                      color={isCurrent ? 'warning.main' : 'text.primary'}
                    >
                      {state.name ?? state.id}
                      {state.initial && <span style={{ color: '#888', marginLeft: 4 }}>(init)</span>}
                      {state.complete && <span style={{ color: '#888', marginLeft: 4 }}>(done)</span>}
                    </Typography>
                  </td>
                  <td><Flag value={state.viewable} /></td>
                  <td><Flag value={state.editable} /></td>
                  <td><Flag value={state.deletable} /></td>
                  <td><ScopeList raw={state.viewScopes} /></td>
                  <td><ScopeList raw={state.editScopes} /></td>
                  <td><ScopeList raw={state.deleteScopes} /></td>
                </tr>
              );
            })}
          </tbody>
        </Box>

        {/* Transitions */}
        {availableTransitions.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
              Transitions from current state
            </Typography>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 11,
                '& td, & th': { p: '2px 6px', verticalAlign: 'middle' },
                '& th': { fontWeight: 600, color: 'text.secondary', textAlign: 'left', whiteSpace: 'nowrap' },
                '& tr:nth-of-type(even)': { backgroundColor: 'action.hover' },
              }}
            >
              <thead>
                <tr>
                  <th>Transition</th>
                  <th>Required scope</th>
                  <th title="user has access">Access</th>
                </tr>
              </thead>
              <tbody>
                {availableTransitions.map((t) => {
                  const requiredScopeId = t.idScope;
                  const requiredScopeDesc = t.scope?.description;
                  const hasAccess = !requiredScopeId ||
                    (userEntityDetails?.scopes ?? []).some((s) => s.id === requiredScopeId);
                  return (
                    <tr key={t.id}>
                      <td>
                        <Chip
                          label={t.labelKey ?? t.actionName ?? `→ ${t.workflowStateTo?.name ?? t.idWorkflowStateTo}`}
                          size="small"
                          sx={{ fontSize: 10, height: 18, backgroundColor: t.displayColor ?? '#005BAF', color: '#fff' }}
                        />
                      </td>
                      <td>
                        {requiredScopeDesc
                          ? <Chip label={requiredScopeDesc} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </td>
                      <td><Flag value={hasAccess} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </>
        )}
      </Paper>
    </Box>,
    document.body
  );
};

export default WorkflowDebugPanel;
